<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import Dialog from '@/views/components/Dialog.vue';
import Confirm from '@/views/components/Confirm.vue';
import { TextBoxComponent } from "@syncfusion/ej2-vue-inputs";
import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/monolith.min.css";
import { Toast } from 'bootstrap';

const router = useRouter();
const route = useRoute();
const store = useFastWay();
const loader = useLoading();

const showToast = (message, variant = 'success') => {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const bgClass = variant === 'success' ? 'bg-success' : variant === 'danger' ? 'bg-danger' : 'bg-primary';

  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 3000 });
  bsToast.show();

  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
};
let id = ref(0);
let loaded = ref(false);
let editing = ref(false);
let dialog = ref(null);
let dialogDelete = ref(null);
let confirm = ref(null);
let data = ref(null);
let colorPickr = null;
let data_local = ref({
  id: 0,
  code: '',
  description: '',
  color: '',
  comments: ''
});
const title = "Áreas de evaluación";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Áreas de evaluación",
    href: "/sbg/projects/evaluation_areas",
  },
  {
    text: "Área de evaluación",
    active: true,
  }
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const initColorPicker = () => {
  colorPickr = Pickr.create({
    el: "#color-picker-container",
    theme: "monolith",
    default: data_local.value.color || "#000000",
    swatches: [
      "rgba(244, 67, 54, 1)",
      "rgba(233, 30, 99, 0.95)",
      "rgba(156, 39, 176, 0.9)",
      "rgba(103, 58, 183, 0.85)",
      "rgba(63, 81, 181, 0.8)",
      "rgba(33, 150, 243, 0.75)",
      "rgba(3, 169, 244, 0.7)",
    ],
    components: {
      preview: false,
      opacity: true,
      hue: true,
      interaction: {
        input: true,
        clear: true,
        save: true,
      },
    },
  });

  const container = document.getElementById("color-picker-container");
  if (container) {
    container.style.display = "none";
  }

  colorPickr.on("save", (color, instance) => {
    if (color) {
      data_local.value.color = color.toHEXA().toString();
    }
    instance.hide();
  });

  colorPickr.on("clear", () => {
    data_local.value.color = "";
  });

  colorPickr.on("cancel", () => {
    colorPickr.hide();
  });
};

const openColorPicker = () => {
  if (editing.value && colorPickr) {
    colorPickr.show();
  }
};

const isValidHex = (hex) => {
  const hexRegex = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(hex);
};

const getTextColor = (hexColor) => {
  if (!hexColor) return '#000000';
  
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const onColorInputChange = () => {
  if (!data_local.value.color) return;
  
  let colorValue = data_local.value.color.trim();
  
  if (isValidHex(colorValue)) {
    if (!colorValue.startsWith('#')) {
      colorValue = '#' + colorValue;
    }
    data_local.value.color = colorValue.toUpperCase();
    if (colorPickr) {
      colorPickr.setColor(colorValue);
    }
  }
};

const onColorInputBlur = () => {
  if (!data_local.value.color || data_local.value.color.trim() === '') {
    data_local.value.color = '#FFFFFF';
    if (colorPickr) {
      colorPickr.setColor('#FFFFFF');
    }
    return;
  }
  
  let colorValue = data_local.value.color.trim();
  
  if (isValidHex(colorValue)) {
    if (!colorValue.startsWith('#')) {
      colorValue = '#' + colorValue;
    }
    data_local.value.color = colorValue.toUpperCase();
    if (colorPickr) {
      colorPickr.setColor(colorValue);
    }
  } else {
    data_local.value.color = '#FFFFFF';
    if (colorPickr) {
      colorPickr.setColor('#FFFFFF');
    }
  }
};

onMounted(async () => {
  id.value = parseInt(route.params.id);
  await getData(id.value);
  initColorPicker();
});

const getData = async (id) => {
  if (id == '0') {
    loaded.value = true;
    editing.value = true;
  } else {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando datos del área de evaluación'});
    await axios
    .post(`${api.value}/sbg/projects/evaluation_areas/data`, {id, context: store.userInfo})
    .then(response => {
      if (response.data.success) {
        if (response.data.data) {
          data.value = response.data.data;
          data_local.value = JSON.parse(JSON.stringify(response.data.data));
        }
      } else {
        dialog.value.show('error', 'Error interno al cargar área de evaluación', response.data.message);
      }
      loaded.value = true;
      loading.hide();
    })
    .catch(error => {
      loaded.value = true;
      loading.hide();
      dialog.value.show('error', `Error al cargar área de evaluación ${id}`, error.message);
    });
  }
};

const resetData = () => {
  if (id.value == '0') {
    router.push({name: 'projectsEvaluationAreas'});
  } else {
    data_local.value = JSON.parse(JSON.stringify(data.value));
    editing.value = false;
    if (colorPickr && data_local.value.color) {
      colorPickr.setColor(data_local.value.color);
    }
  }
};

const validateForm = () => {
  let validated = false;
  if (!data_local.value.code || data_local.value.code == '') {
    dialog.value.show('error', 'Error al guardar área de evaluación', 'Debe especificar un código');
  } else if (!data_local.value.description || data_local.value.description == '') {
    dialog.value.show('error', 'Error al guardar área de evaluación', 'Debe especificar la descripción');
  } else if (!data_local.value.color || data_local.value.color == '') {
    dialog.value.show('error', 'Error al guardar área de evaluación', 'Debe especificar el color');
  } else {
    validated = true;
  }
  return validated;
};

const save = async () => {
  if (validateForm()) {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Guardando datos del área de evaluación'});
    data_local.value.id = id.value;
    data_local.value.context = store.userInfo;
    data_local.value.createdBy = store.userInfo.userId;
    await axios
    .post(`${api.value}/sbg/projects/evaluation_areas/save`, data_local.value)
    .then(response => {
      loading.hide();
      if (response.data.success) {
        data.value = JSON.parse(JSON.stringify(data_local.value));
        editing.value = false;
        showToast('Datos han sido actualizados', 'success');
        if (id.value == 0) {
          router.push({
            name: 'projectsEvaluationArea',
            params: {id: response.data.data.id}
          });
        }
      } else {
        showToast(response.data.message, 'danger');
      }
    })
    .catch(() => {
      loading.hide();
      showToast(`Error al actualizar área de evaluación ${id.value}`, 'danger');
    });
  }
};

const remove = async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Eliminando área de evaluación'});
  const parms = {
    id: id.value,
    context: store.userInfo,
    createdBy: store.userInfo.userId
  };
  await axios
  .post(`${api.value}/sbg/projects/evaluation_areas/delete`, parms)
  .then(response => {
    loading.hide();
    if (response.data.success) {
      dialogDelete.value.show('success', 'Información', 'Área de evaluación fue eliminada correctamente');
    } else {
      dialog.value.show('error', 'Error al eliminar', response.data.message);
    }
  })
    .catch(() => {
      loading.hide();
      showToast(`Error al actualizar área de evaluación ${id.value}`, 'danger');
    });
};

const add = () => {
  router.push({
    name: 'projectsEvaluationArea',
    params: {id: 0}
  });
  id.value = 0;
  data_local.value = {
    id: 0,
    code: '',
    description: '',
    color: '',
    comments: ''
  };
  editing.value = true;
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
          <div class="card mb-2">
              <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center align-middle row ms-0 me-0 mb-2">
                      <div v-if="!editing" class="col d-flex justify-content-start">
                        <b-button variant="success" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Agregar área de evaluación" @click="add"><i class="las la-plus label-icon"></i> Agregar</b-button>
                      </div>
                      <div class="col"/>
                      <div class="input-group col justify-content-end">
                        <b-button v-if="editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Guardar cambios efectuados" @click="save"><i class="las la-save label-icon"></i> Guardar</b-button>
                        <b-button v-if="editing" variant="warning" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Descartar cambios efectuados" @click="resetData"><i class="las la-undo label-icon"></i> Cancelar</b-button>
                        <b-button v-if="!editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Editar información" @click="editing = true"><i class="las la-edit label-icon"></i> Editar</b-button>
                        <b-button v-if="!editing" variant="danger" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Eliminar área de evaluación" @click="confirm.show('Confirme', '¿Está seguro de eliminar esta área de evaluación?')"><i class="las la-trash label-icon"></i> Eliminar</b-button>
                      </div>
                  </div>
                  <hr>
                  <div class="row mb-4">
                    <div class="col-4">
                      <div class="form-floating">
                        <input type="text" class="form-control form-control-lg" :class="{'bg-light': !editing, 'bg-white': editing}" id="code" v-model="data_local.code" placeholder="Código" :disabled="!editing"/>
                        <label for="code">Código</label>
                      </div>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col">
                      <TextBoxComponent v-model:value="data_local.description" floatLabelType="Auto" placeholder="Descripción" :disabled="!editing"/>
                    </div>
                    <div class="col-4">
                      <div class="form-floating position-relative">
                        <input type="text" class="form-control color-input" id="color-display" v-model="data_local.color" placeholder="Color" :disabled="!editing" :style="{backgroundColor: data_local.color || '', color: getTextColor(data_local.color)}" @input="onColorInputChange" @blur="onColorInputBlur"/>
                        <label for="color-display">Color</label>
                        <span v-if="editing" class="color-picker-icon" v-b-tooltip.hover title="Seleccionar color" @click="openColorPicker"><i class="las la-palette"></i></span>
                      </div>
                      <div id="color-picker-container" class="mt-2" v-show="editing"></div>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col">
                      <TextBoxComponent v-model:value="data_local.comments" floatLabelType="Auto" placeholder="Comentarios" :enabled="editing" :multiline="true"/>
                    </div>
                  </div>
              </div>
          </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
    <Dialog ref="dialogDelete" @accept="router.push({name: 'projectsEvaluationAreas'});"/>
    <Confirm ref="confirm" @accept="remove"/>
    <div id="toast-container" class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"></div>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-buttons/styles/material.css";
@import "../../../../node_modules/@simonwep/pickr/dist/themes/monolith.min.css";

.e-input-group .e-input[disabled], .e-input-group.e-control-wrapper .e-input[disabled], .e-input-group.e-disabled, .e-input-group.e-control-wrapper.e-disabled, .e-float-input input[disabled], .e-float-input.e-control-wrapper input[disabled], .e-float-input textarea[disabled], .e-float-input.e-control-wrapper textarea[disabled], .e-float-input.e-disabled, .e-float-input.e-control-wrapper.e-disabled {
    -webkit-text-fill-color: rgba(0, 0, 0, 0.75);
    color: rgba(0, 0, 0, 0.75);
}

.color-picker-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 10;
    font-size: 1.25rem;
    color: rgba(0, 0, 0, 0.5);
}

.color-picker-icon:hover {
    color: rgba(0, 0, 0, 0.8);
}

.color-input {
    font-size: 0.875rem;
    height: 2.5rem;
    line-height: 1.5;
}

#color-display:disabled {
    cursor: default;
}

label[for="color-display"] {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 0.25rem;
}
</style>

<style>
.pcr-button,
.pcr-trigger,
.pcr-app .pcr-interaction .pcr-preview,
.pcr-app .pcr-result,
.pcr-app [class*="preview"],
.pcr-app[data-theme="monolith"] .pcr-preview,
#color-picker-container .pcr-button,
#color-picker-container .pcr-trigger {
    display: none !important;
}
</style>
